/**
 * /admin/reports/trial-balance — Trial Balance.
 * @file src/web/features/admin/pages/reports/trial-balance.tsx
 *
 * Every ledger's closing balance in Dr/Cr columns, grouped by account
 * group, with group subtotals and a Debits = Credits tie-out. Scoped to
 * the sidebar entity. Click a ledger → its statement.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scale, CheckCircle2, AlertTriangle, ChevronLeft } from 'lucide-react';
import { SEO } from '../../../../shared/components';
import { AdminPageHeader } from '../../components/AdminPageHeader';
import { PageLoading } from '../../components/PageLoading';
import { adminFetchJson } from '../../lib/admin-api';
import { useCompany } from '../../lib/company-context';
import { usePeriod } from '../../lib/period-context';
import { PeriodBar } from '../../components/PeriodBar';

interface Row { id: string; name: string; debit: number; credit: number }
interface Group { name: string; debit: number; credit: number; ledgers: Row[] }
interface TB { groups: Group[]; totals: { debit: number; credit: number; balanced: boolean } }

const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');

export default function TrialBalancePage() {
  const { entityId } = useCompany();
  const { to } = usePeriod();
  const [data, setData] = useState<TB | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setData(null); setError(null);
    adminFetchJson<TB>('/api/tally-import/trial-balance', { query: { entityId: entityId ?? undefined, to: to || undefined } })
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [entityId, to]);

  return (
    <>
      <SEO title="Trial Balance" />
      <AdminPageHeader title="Trial Balance" breadcrumbs={[{ label: 'Reports', href: '/admin/reports' }, { label: 'Trial Balance' }]} />

      <Link to="/admin/reports" className="mb-4 inline-flex items-center gap-1 text-sm text-ink-500 no-underline hover:text-ink-700">
        <ChevronLeft className="h-4 w-4" /> All reports
      </Link>

      <PeriodBar />

      {error &&<div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}
      {!data && !error && <PageLoading label="Building trial balance" />}

      {data && data.groups.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-ink-100 text-ink-400"><Scale className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold text-ink-900">Nothing to show</h3>
          <p className="mt-1.5 text-sm text-ink-600">Import Tally data for this entity to see its trial balance.</p>
        </div>
      )}

      {data && data.groups.length > 0 && (
        <>
          <div className="mb-4 flex items-center justify-between">
            {data.totals.balanced
              ? <span className="inline-flex items-center gap-1.5 rounded-full bg-ok-50 px-3 py-1 text-xs font-medium text-ok-600"><CheckCircle2 className="h-3.5 w-3.5" /> Balanced — Debits = Credits</span>
              : <span className="inline-flex items-center gap-1.5 rounded-full bg-warn-50 px-3 py-1 text-xs font-medium text-warn-600"><AlertTriangle className="h-3.5 w-3.5" /> Out of balance</span>}
          </div>

          <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
            <table className="w-full min-w-[36rem] text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-400">
                  <th className="px-5 py-3 text-left font-medium">Particulars</th>
                  <th className="px-5 py-3 text-right font-medium">Debit</th>
                  <th className="px-5 py-3 text-right font-medium">Credit</th>
                </tr>
              </thead>
              <tbody>
                {data.groups.map((g) => (
                  <GroupBlock key={g.name} group={g} />
                ))}
                <tr className="border-t-2 border-ink-200 bg-ink-50 font-semibold text-ink-900">
                  <td className="px-5 py-3">Total</td>
                  <td className="px-5 py-3 text-right tabular-nums">{inr(data.totals.debit)}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{inr(data.totals.credit)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function GroupBlock({ group: g }: { group: Group }) {
  return (
    <>
      <tr className="border-t border-ink-100 bg-ink-50/60">
        <td className="px-5 py-2 text-xs font-semibold uppercase tracking-wide text-ink-500">{g.name}</td>
        <td className="px-5 py-2 text-right text-xs font-semibold tabular-nums text-ink-500">{inr(g.debit)}</td>
        <td className="px-5 py-2 text-right text-xs font-semibold tabular-nums text-ink-500">{inr(g.credit)}</td>
      </tr>
      {g.ledgers.map((l) => (
        <tr key={l.id} className="border-t border-ink-50 hover:bg-ink-50/60">
          <td className="px-5 py-2 pl-8">
            <Link to={`/admin/ledgers/${l.id}`} className="text-ink-700 no-underline hover:text-inkblack">{l.name}</Link>
          </td>
          <td className="px-5 py-2 text-right tabular-nums text-ink-800">{inr(l.debit)}</td>
          <td className="px-5 py-2 text-right tabular-nums text-ink-800">{inr(l.credit)}</td>
        </tr>
      ))}
    </>
  );
}
